import React from 'react';
import { Column } from '../../components/DataTable/DataTable';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { VesselSparePendingPurchaseOrderRow } from './useData';

export function createVesselSparePendingDeliveriesModuleConfig(vesselId: number) {
  const base = `/spare-pending-deliveries`;
  void vesselId;
  const routes = {
    list: base,
    new: base,
    detail: (id: number) => `${base}/${id}`,
    edit: (id: number) => `${base}/${id}`,
  };

  const moduleConfig = {
    module: {
      title: 'Spare pending deliveries',
      permissionModuleKey: 'vessel_spare_pending_deliveries',
      route: routes,
    },
    pagination: {
      initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
      itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
    },
    search: { placeholder: 'Search by order ref, case, quotation, supplier…' },
    filterGroups: [{ name: 'basic', label: 'Basic', icon: '', fields: [] }],
    filters: {
      fields: [],
      applyFilter: (items: VesselSparePendingPurchaseOrderRow[]) => items,
      applySearch: (items: VesselSparePendingPurchaseOrderRow[], searchTerm: string) => {
        const q = searchTerm.toLowerCase().trim();
        if (!q) return items;
        return items.filter((item) => {
          const parts = [
            item.ref,
            item.spareCase?.ref,
            item.quotation?.ref,
            String(item.id),
            item.supplier?.name,
            item.supplier?.code,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return parts.includes(q);
        });
      },
    },
    toolbar: { actions: [] },
    table: {
      actionsPosition: 'left' as const,
      onRowClick: (item: VesselSparePendingPurchaseOrderRow) => {
        window.location.href = routes.detail(item.id);
      },
      keyExtractor: (item: VesselSparePendingPurchaseOrderRow) => item.id,
    },
    emptyStates: {
      noData: 'No pending deliveries. Quantities on purchase orders are fully received.',
      noSearchResults: 'No orders match your search.',
      noFilterResults: 'No orders match the filters.',
    },
    delete: {
      title: 'Remove',
      message: 'Not available.',
      confirmText: 'OK',
      cancelText: 'Cancel',
      errorMessage: 'Not supported.',
      handler: async () => {},
    },
    utils: {
      formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : ''),
    },
    form: {
      new: { title: 'Spare pending deliveries', submitText: 'OK' },
      edit: { title: 'Spare pending deliveries', submitText: 'OK' },
      cancelText: 'Cancel',
      loadingText: 'Loading...',
      errorMessage: 'Error.',
    },
    fields: [],
  };

  return { moduleConfig, routes };
}

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export function getColumns(
  formatDate: (dateString?: string) => string,
): Column<VesselSparePendingPurchaseOrderRow>[] {
  return [
    {
      key: 'ref',
      header: 'Order ref',
      sortable: true,
      render: (v, row) => <TextCell value={v ?? row?.ref} />,
    },
    {
      key: 'orderDate',
      header: 'Order date',
      sortable: true,
      render: (v) => <span className="text-sm">{formatDate(v)}</span>,
    },
    {
      key: 'spareCase.ref',
      header: 'Case',
      sortable: true,
      render: (_v, row) => <TextCell value={row?.spareCase?.ref} />,
    },
    {
      key: 'quotation.ref',
      header: 'Quotation',
      sortable: true,
      render: (_v, row) => <TextCell value={row?.quotation?.ref} />,
    },
    {
      key: 'supplier.name',
      header: 'Supplier',
      sortable: true,
      render: (_v, row) => (
        <TextCell
          value={
            row?.supplier?.name
              ? row.supplier.name
              : row?.supplierId != null
                ? String(row.supplierId)
                : ''
          }
        />
      ),
    },
    {
      key: 'pendingLineCount',
      header: 'Pending lines',
      sortable: true,
      align: 'right',
      render: (_v, row) => <span className="text-sm tabular-nums">{row?.pendingLineCount ?? 0}</span>,
    },
    {
      key: 'totalPendingQty',
      header: 'Pending qty',
      sortable: true,
      align: 'right',
      render: (_v, row) => <span className="text-sm tabular-nums">{row?.totalPendingQty ?? 0}</span>,
    },
  ];
}

export function getToolbarIcon(icon: string): React.ReactNode {
  return <span>{icon}</span>;
}
